import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { PaxAmount } from '@fcom/dapi';
import { GlobalBookingTravelClass } from '@fcom/core';
import { ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { PopoverOptions } from '@fcom/ui-components/components/popover/popover.interface';
import { PopoverService } from '@fcom/ui-components/components/popover/service/popover.service';
import { LanguageService } from '@fcom/ui-translate';

import { AmountUpdateType, PaxAmountTypeConfig, PaxUpdateEvent } from '../../interfaces/paxAmount.interface';
import { SelectorType } from '../../interfaces/booking-widget.interface';
import { defaultWidgetPopoverOptions } from '../../constants';
import { BookingWidgetService } from '../../services/booking-widget.service';
import { mapPaxAmountToPaxBreakdown } from '../../utils/utils.pax-amount';
import { BookingWidgetTripService } from '../../services/booking-widget-trip.service';
import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';

const MAX_PAX_AMOUNT = 9;
const MIN_LEAD_PAX_AMOUNT = 1;

@Component({
  selector: 'fin-pax-amount-selector',
  templateUrl: './pax-amount-selector.component.html',
  styleUrls: ['./pax-amount-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaxAmountSelectorComponent {
  private popoverService = inject(PopoverService);
  private bookingWidgetService = inject(BookingWidgetService);
  private languageService = inject(LanguageService);
  private bookingWidgetTripService = inject(BookingWidgetTripService);
  private bookingWidgetGtmService = inject(BookingWidgetGtmService);

  protected readonly ButtonTheme = ButtonTheme;
  protected readonly SvgLibraryIcon = SvgLibraryIcon;

  readonly isSearchEnabled = input(false);

  readonly disabled = input(false);

  readonly setTravelClass = output<GlobalBookingTravelClass>();

  readonly setPaxAmount = output<PaxUpdateEvent>();

  readonly enableNewSearchAutomatically = input(false);

  readonly startSearch = output<void>();

  readonly travelClass = this.bookingWidgetTripService.selectedTravelClass;
  readonly availableTravelClasses = this.bookingWidgetTripService.availableTravelClasses;
  readonly GlobalBookingTravelClass = GlobalBookingTravelClass;
  readonly paxAmount = this.bookingWidgetTripService.paxAmount;
  readonly AmountUpdateType = AmountUpdateType;
  readonly IconPosition = IconPosition;

  readonly SelectorType = SelectorType;
  readonly popoverOptions: PopoverOptions = {
    ...defaultWidgetPopoverOptions,
    popoverID: 'paxAmountSelectorPopover',
    disableAutoFocus: true,
  };

  modalOpen = false;

  private readonly passengerTranslations = toSignal(
    this.languageService.translate<Record<string, string>>('passenger')
  );

  readonly totalPax = computed(() => Object.values(this.paxAmount()).reduce((acc, val) => acc + val, 0));

  readonly paxAmountBreakdown = computed(() => {
    const translations = this.passengerTranslations();
    return translations ? mapPaxAmountToPaxBreakdown(this.paxAmount(), translations) : '';
  });

  readonly paxAmountConfig = computed<PaxAmountTypeConfig>(() => {
    const paxAmount = this.paxAmount();
    return {
      adults: {
        min: MIN_LEAD_PAX_AMOUNT,
        max: MAX_PAX_AMOUNT - paxAmount.c15s - paxAmount.children,
      },
      c15s: {
        min: 0,
        max: MAX_PAX_AMOUNT - paxAmount.adults - paxAmount.children,
      },
      children: {
        min: 0,
        max: MAX_PAX_AMOUNT - paxAmount.adults - paxAmount.c15s,
      },
      infants: { min: 0, max: paxAmount.adults },
    };
  });

  readonly usePopoverSelectors = this.bookingWidgetService.usePopoverSelectors();

  openModal(): void {
    if (!this.usePopoverSelectors) {
      this.modalOpen = true;
    }
  }

  onTravelClassSelect(selectedTravelClass: GlobalBookingTravelClass): void {
    this.bookingWidgetTripService.setTravelClass(selectedTravelClass);
  }

  onPaxDetailsUpdate(amount: number, type: string, updateType: AmountUpdateType): void {
    this.setPaxAmount.emit({ paxType: type as keyof PaxAmount, amount, updateType });
  }

  closePopover(): void {
    this.popoverService.close();
  }

  cancelSelection(): void {
    const originalSelection = this.bookingWidgetService.currentSearch();
    this.bookingWidgetService.setPaxAmount(originalSelection.paxAmount);
    this.closePopover();
    this.bookingWidgetGtmService.trackElementEvent('global-widget-cancel-search-from-pax-amount');
  }

  onClose(): void {
    if (this.enableNewSearchAutomatically()) {
      this.cancelSelection();
      this.bookingWidgetGtmService.trackElementEvent('global-widget-cancel-search-from-pax-amount');
    }
  }

  startNewSearch(): void {
    this.startSearch.emit();
    this.bookingWidgetGtmService.trackElementEvent('global-widget-start-new-search-from-pax-amount');
  }
}
